import React, { useEffect, useState } from "react";
import { Box, IconButton, MenuItem, Select, Typography } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { contrastColor, hexToRgba } from "../utils";
import MobileCalendar from "./MobileCalendar";
import CategoryEditor from "./CategoryEditor";
import { parseDate } from "./Calendar";

export default function MobileApp({
  data,
  onYearChange,
  openDayModal,
  addCategory,
  updateCategory,
  deleteCategory,
  PALETTE,
  onOpenSettings,
}) {
  const [activeCatId, setActiveCatId] = useState(null);
  const [anchorDate, setAnchorDate] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorInitial, setEditorInitial] = useState(null);

  const activeCat = data.categories.find((c) => c.id === activeCatId) || null;

  // reset a pending selection when switching years
  useEffect(() => {
    setAnchorDate(null);
  }, [data.year]);

  function buildRange(a, b) {
    const start = parseDate(a);
    const end = parseDate(b);
    const inc = start <= end ? 1 : -1;
    const dates = [];
    const cur = new Date(start);
    while (true) {
      dates.push(
        `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(cur.getDate()).padStart(2, "0")}`
      );
      if (
        cur.getFullYear() === end.getFullYear() &&
        cur.getMonth() === end.getMonth() &&
        cur.getDate() === end.getDate()
      )
        break;
      cur.setDate(cur.getDate() + inc);
    }
    if (inc === -1) dates.reverse();
    return dates;
  }

  // Tap once to anchor a selection, tap a second day to edit the range.
  // Tapping the same day twice edits just that day (single-day modal).
  function handleDayTap(date) {
    if (!anchorDate) {
      setAnchorDate(date);
      return;
    }
    if (anchorDate === date) {
      setAnchorDate(null);
      openDayModal(date);
      return;
    }
    const dates = buildRange(anchorDate, date);
    setAnchorDate(null);
    openDayModal(
      activeCat
        ? { type: "multi", dates, swapTo: activeCat.id, half: false }
        : { type: "multi", dates }
    );
  }

  function handleDayLongPress(date) {
    setAnchorDate(null);
    openDayModal(date);
  }

  return (
    <Box className="mobile-app">
      <div className="mobile-topbar">
        <header className="mobile-header">
          <EventAvailableIcon className="mobile-logo" />
          <Typography variant="h6" className="mobile-title">
            <strong>Time</strong>Off<strong>Tool</strong>
          </Typography>
          <div className="flex-1" />
          <Select
            size="small"
            value={data.year}
            onChange={(e) => onYearChange(+e.target.value)}
          >
            {Array.from({ length: 6 }).map((_, i) => {
              const y = 2025 + i;
              return (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              );
            })}
          </Select>
          <IconButton aria-label="Settings" onClick={onOpenSettings}>
            <SettingsIcon />
          </IconButton>
        </header>

        <div className="cat-chips">
          {data.categories.map((cat) => {
            const used = cat.used || 0;
            const remaining = Math.max(0, cat.qty - used);
            const isActive = activeCatId === cat.id;
            return (
              <button
                key={cat.id}
                className={"cat-chip" + (isActive ? " active" : "")}
                style={
                  isActive
                    ? {
                        background: cat.color,
                        color: contrastColor(cat.color),
                        borderColor: cat.color,
                      }
                    : { borderColor: hexToRgba(cat.color, 0.5) }
                }
                onClick={() => setActiveCatId(isActive ? null : cat.id)}
              >
                {!isActive && (
                  <span className="chip-dot" style={{ background: cat.color }} />
                )}
                <span>{cat.name}</span>
                <span className="chip-count">{remaining} left</span>
                {isActive && (
                  <span
                    className="chip-edit"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditorInitial(cat);
                      setEditorOpen(true);
                    }}
                  >
                    <EditIcon fontSize="inherit" />
                  </span>
                )}
              </button>
            );
          })}
          <button
            className="cat-chip add-chip"
            onClick={() => {
              setEditorInitial(null);
              setEditorOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> New
          </button>
        </div>
      </div>

      <div className="mobile-hint">
        {anchorDate ? (
          <>
            Tap the last day of the range — or tap the same day again to edit
            just that day.
            <button className="hint-cancel" onClick={() => setAnchorDate(null)}>
              Cancel
            </button>
          </>
        ) : activeCat ? (
          <>
            Tap a start day, then an end day, to apply{" "}
            <strong>{activeCat.name}</strong>. Long-press a day for quick
            options.
          </>
        ) : (
          <>
            Tap a day, then another, to select a range to edit. Long-press a day
            for quick options.
          </>
        )}
      </div>

      <MobileCalendar
        data={data}
        anchorDate={anchorDate}
        onDayTap={handleDayTap}
        onDayLongPress={handleDayLongPress}
      />

      <CategoryEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        initial={editorInitial || {}}
        PALETTE={PALETTE}
        onSave={(payload) => {
          if (payload.id)
            updateCategory(payload.id, payload.name, payload.qty, payload.color);
          else addCategory && addCategory(payload.name, payload.qty, payload.color);
        }}
        onDelete={(id) => {
          deleteCategory(id);
          if (activeCatId === id) setActiveCatId(null);
        }}
      />
    </Box>
  );
}
